import { FC, useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import ReCAPTCHA from "react-google-recaptcha";

type InviteStatus = 'validating' | 'valid' | 'invalid' | 'success';

const InvitePage: FC = () => {
    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();

    const [status, setStatus] = useState<InviteStatus>('validating');
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);

    useEffect(() => {
        const checkInviteCode = async () => {
            if (!code) {
                setStatus('invalid');
                return;
            }
            try {
                const response = await api.get(`/api/users/checkInvite/${code}`);
                if (response.data.status && response.data.data === true) {
                    setStatus('valid');
                } else {
                    throw new Error("Link non valido o scaduto.");
                }
            } catch (err) {
                setStatus('invalid');
            }
        };
        checkInviteCode();
    }, [code]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!agreedToTerms) {
            setError("Devi accettare il trattamento dei dati personali.");
            return;
        }

       // if (!captchaToken) {
       //     setError("Devi completare la verifica reCAPTCHA.");
       //     return;
       // }

        if (formData.password !== formData.confirmPassword) {
            setError("Le password non coincidono.");
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                code,
                name: formData.name,
                surname: formData.surname,
                email: formData.email,
                password: formData.password,
                captchaToken: captchaToken
            };
            const response = await api.post('/api/users/register', payload);
            if (response.data.status) {
                setStatus('success');
            } else {
                throw new Error(response.data.message || "Errore durante la registrazione.");
            }
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || "Si è verificato un errore.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'validating':
                return <p className="text-slate-400">Verifica del link di invito in corso...</p>;

            case 'invalid':
                return (
                    <div>
                        <h2 className="text-xl font-bold text-red-400 mb-4">Link non valido</h2>
                        <p className="text-slate-400">Questo link di invito non è valido o potrebbe essere scaduto. Richiedi un nuovo invito.</p>
                        <Link to="/login" className="mt-6 inline-block text-sky-400 hover:text-sky-300">Torna al Login</Link>
                    </div>
                );

            case 'success':
                return (
                    <div>
                        <h2 className="text-xl font-bold text-green-400 mb-4">Registrazione Completata!</h2>
                        <p className="text-slate-400">Ti abbiamo inviato un'email. Clicca sul link di conferma per attivare il tuo account.</p>
                        <p className="text-slate-500 text-sm mt-2">Il tuo account dovrà essere approvato da un amministratore prima di poter accedere.</p>
                        <Link to="/login" className="mt-6 inline-block text-sky-400 hover:text-sky-300">Torna al Login</Link>
                    </div>
                );

            case 'valid':
                return (
                    <>
                        <h2 className="text-xl font-bold text-white mb-4">Crea il tuo Account</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">Nome</label>
                                    <input type="text" name="name" id="name" required value={formData.name} onChange={handleInputChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                                </div>
                                <div>
                                    <label htmlFor="surname" className="block text-sm font-medium text-slate-300 mb-2">Cognome</label>
                                    <input type="text" name="surname" id="surname" required value={formData.surname} onChange={handleInputChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                                <input type="email" name="email" id="email" required value={formData.email} onChange={handleInputChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                            </div>
                            <div>
                                <label htmlFor="password"  className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                                <input type="password" name="password" id="password" required value={formData.password} onChange={handleInputChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                            </div>
                            <div>
                                <label htmlFor="confirmPassword"  className="block text-sm font-medium text-slate-300 mb-2">Conferma Password</label>
                                <input type="password" name="confirmPassword" id="confirmPassword" required value={formData.confirmPassword} onChange={handleInputChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                            </div>
                            <div>
                                <label htmlFor="termsCheckbox" className="flex items-center text-sm font-medium text-slate-300">
                                    <input
                                        type="checkbox"
                                        id="termsCheckbox"
                                        checked={agreedToTerms}
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        required
                                        className="form-checkbox h-4 w-4 bg-slate-800 border-slate-600 rounded text-sky-500 mr-2 focus:ring-sky-500"
                                    />
                                    <span>
                                        Acconsento al
                                        <a href="/privacy-policy" target="_blank" className="text-sky-400 hover:underline px-1">trattamento dei dati personali</a>
                                        e accetto i
                                        <a href="/terms-and-conditions" target="_blank" className="text-sky-400 hover:underline px-1">Termini e condizioni</a>.
                                    </span>
                                </label>
                            </div>
                            {/*<ReCAPTCHA
                                sitekey={process.env.REACT_APP_RECAPTCHA || ""}
                                onChange={(token: any) => setCaptchaToken(token)}
                            />*/}
                            {error && <p className="text-red-400 text-sm">{error}</p>}
                            <button type="submit" disabled={isLoading} className="w-full px-5 py-3 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 transition-colors disabled:bg-slate-600">
                                {isLoading ? 'Registrazione...' : 'Registrati'}
                            </button>
                            <p className="mt-6 text-center text-slate-400">
                                Hai già un account? <Link to="/login" className="text-sky-400 hover:text-sky-300">Accedi</Link>
                            </p>
                        </form>
                    </>
                );
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-white">Benvenuto!</h1>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default InvitePage;
